import { Box } from '@eatclubapp/ec-component-library';
import { useTheme } from '@material-ui/core';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';
import { Button } from '../../../../../components/Button';
import { Typography } from '../../../../../components/Typography';
import { getContrastTextColor } from '../../../../../utils/themeHelpers';
import useStyles from './CalendarStyles';

const DateCell = memo(
  ({
    date,
    isDisabled,
    experiencesFull,
    isSelected,
    isToday,
    isMobile,
    onDateChange,
    getTileBgColor,
    getNumberBackgroundColor,
  }) => {
    const theme = useTheme();
    const classes = useStyles();

    const tileBgColor = getTileBgColor(isDisabled);
    const numberBackgroundColor = getNumberBackgroundColor(isDisabled, isSelected);
    const isFull = false;

    // Memoize calculations and styles
    const buttonStyle = useMemo(
      () => ({
        background: `${tileBgColor} !important`,
        border: isFull ? `1px solid ${theme.colors.cloud}` : '1px solid transparent',
        height: isMobile ? '36px' : '56px',
        minWidth: 'unset',
        padding: 'unset',
        position: 'relative',
      }),
      [tileBgColor, isDisabled, isFull, theme.colors.cloud, isMobile],
    );

    const boxStyle = useMemo(
      () => ({
        alignItems: 'center',
        border: isSelected
          ? `1px solid currentColor`
          : isToday && `1px solid ${theme.colors.outlineColour}`,
        color:
          !isSelected && theme.colors.darkMode
            ? theme.colors.mainTextColour
            : isDisabled
            ? theme.colors.nimbus
            : getContrastTextColor(numberBackgroundColor),
        backgroundColor: numberBackgroundColor,
        borderRadius: '100%',
        display: 'flex',
        height: isMobile ? '28px' : '32px',
        justifyContent: 'center',
        width: isMobile ? '28px' : '32px',
      }),
      [
        isSelected,
        isToday,
        theme.colors,
        isDisabled,
        isMobile,
        numberBackgroundColor,
        getContrastTextColor,
      ],
    );

    // Memoize click handler
    const handleClick = useCallback(() => {
      onDateChange(date);
    }, [date, onDateChange]);

    return (
      <Button variant='text' onClick={handleClick} disabled={isDisabled} style={buttonStyle}>
        {experiencesFull && <Box className={classes.fullDot} />}
        <Box style={boxStyle}>
          <Typography
            variant='small'
            style={{
              fontWeight: !isSelected && !isToday && theme.fontWeights.regular,
            }}
          >
            {date.format('D')}
          </Typography>

          {isFull && (
            <Typography className={classes.fullPill} variant='caption'>
              FULL
            </Typography>
          )}
        </Box>
      </Button>
    );
  },
);

DateCell.propTypes = {
  date: PropTypes.instanceOf(moment).isRequired,
  isDisabled: PropTypes.bool.isRequired,
  experiencesFull: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isToday: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  onDateChange: PropTypes.func.isRequired,
  getTileBgColor: PropTypes.func.isRequired,
  getNumberBackgroundColor: PropTypes.func.isRequired,
};

export default DateCell;
